import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BadgePercent, Sparkles } from "lucide-react";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { currency } from "@/lib/format";
import { usePropertyAccess } from "@/lib/property-access";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { BookingDetailDialog } from "@/components/resource/booking-detail";
import type { Booking } from "@/types/domain";

type PendingDiscount = {
  id: string; amount: string; type: "AMOUNT" | "PERCENT"; value: string; reason: string; createdAt: string;
  requestedBy: { id: string; name: string };
  booking: { id: string; bookingRef: string; guestName: string; totalAmount: string };
};
type AttentionRoom = { id: string; name: string; reason: "MAINTENANCE" | "NEEDS_CLEANING" };

// Today's to-dos on the dashboard: discounts waiting for the main owner, and rooms to clean.
export function DashboardTasks({ propertyId, attentionRooms }: { propertyId: string; attentionRooms: AttentionRoom[] }) {
  const access = usePropertyAccess();
  const role = useAppStore((state) => state.user?.role);
  const [openBooking, setOpenBooking] = useState<Booking | null>(null);
  const discounts = useQuery({
    queryKey: ["bookings", propertyId, "discount-requests"],
    queryFn: () => api<PendingDiscount[]>(`/dashboard/discount-requests?propertyId=${propertyId}`),
    enabled: access.canManageMembers,
  });
  const markClean = useMutation({
    mutationFn: (roomId: string) => api(`/rooms/${roomId}`, { method: "PATCH", body: { isClean: true } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bookings", propertyId, "dashboard"] }),
  });
  const dirty = attentionRooms.filter((room) => room.reason === "NEEDS_CLEANING");
  const pending = discounts.data ?? [];
  const open = async (bookingId: string) => setOpenBooking(await api<Booking>(`/bookings/${bookingId}`));
  if (!pending.length && !dirty.length) return null;

  return (
    <section className="dash-section dash-tasks" aria-label="To do">
      {pending.length > 0 && (
        <div className="card dash-task-card">
          <div className="card__head"><h2><BadgePercent size={18} aria-hidden="true" /> Discount approvals ({pending.length})</h2></div>
          <ul className="dash-task-list">
            {pending.map((request) => (
              <li key={request.id}>
                <span>
                  <strong>{currency.format(Number(request.amount))}{request.type === "PERCENT" ? ` (${Number(request.value)}%)` : ""} off {request.booking.bookingRef}</strong>
                  <small>{request.requestedBy.name} · {request.booking.guestName} · {request.reason}</small>
                </span>
                <Button variant="secondary" onClick={() => void open(request.booking.id)}>Review</Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dirty.length > 0 && (
        <div className="card dash-task-card">
          <div className="card__head"><h2><Sparkles size={18} aria-hidden="true" /> Housekeeping ({dirty.length})</h2></div>
          <ul className="dash-task-list">
            {dirty.map((room) => (
              <li key={room.id}>
                <span><strong>{room.name}</strong><small>Guest left - clean before the next arrival</small></span>
                {access.can("CHECK_IN_OUT") && <Button variant="secondary" loading={markClean.isPending && markClean.variables === room.id} onClick={() => markClean.mutate(room.id)}>Mark clean</Button>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {openBooking && (
        <BookingDetailDialog
          booking={openBooking}
          role={role === "ADMIN" ? "ADMIN" : "OWNER"}
          onClose={() => { setOpenBooking(null); void queryClient.invalidateQueries({ queryKey: ["bookings", propertyId] }); }}
        />
      )}
    </section>
  );
}
